import i18next from "i18next";
import { initReactI18next } from "react-i18next";
import pl from "@locales/pl.json";
import en from "@locales/en.json";
import common from "@locales/common";
import monitoringTranslations from "@locales/monitoring";
import componentsTranslations from "@locales/components";
import taskTranslations from "@locales/tasks";
import programPreviewTranslations from "@locales/program_preview";
import programScheduleTranslations from "@locales/program_schedule";
import patientTranslations from "@locales/patient";
import integration from "@locales/integrations";
import empty_states from "@locales/empty_states";
import settings from "@locales/settings";
import cooperation_settings from "@locales/cooperation_settings";
import educationalMaterials from "@locales/educational_materials";
import autogenerate from "@locales/autogenerate_modal";
import sharedTranslations from "@locales/patient/shared";
import questionnairesTranslations from "@locales/questionnaires";
import chatTranslations from "@locales/chat";
import auth from "@locales/auth";
import validation from "@locales/validation";
import companySettingsTabs from "@locales/company_settings_tabs";
import diet from "@locales/diet";
import subscriptions from "@locales/subscriptions";
import schedule from "@locales/schedule";
import patientFiles from "@locales/patient_files";
import aiOnboarding from "@locales/ai_onboarding";
import aiNotes from "@locales/ai_notes";
import programPdf from "@locales/program_pdf";

import uiEn from "@UI/locales/en";
import uiPl from "@UI/locales/pl";

/** Deep merge two objects — nested keys from `b` override `a`, but sibling keys are preserved. */
function deepMerge<T extends Record<string, unknown>>(
  a: T,
  b: Record<string, unknown>,
): T {
  const result = { ...a } as Record<string, unknown>;
  for (const key of Object.keys(b)) {
    if (
      result[key] &&
      typeof result[key] === "object" &&
      !Array.isArray(result[key]) &&
      typeof b[key] === "object" &&
      !Array.isArray(b[key])
    ) {
      result[key] = deepMerge(
        result[key] as Record<string, unknown>,
        b[key] as Record<string, unknown>,
      );
    } else {
      result[key] = b[key];
    }
  }
  return result as T;
}

i18next.use(initReactI18next).init({
  resources: {
    pl: {
      translation: {
        ...deepMerge(uiPl, pl as Record<string, unknown>),
        common: { ...uiPl.common, ...common.pl },
        diet: diet.pl,
        schedule: schedule.pl,
        monitoring: monitoringTranslations.pl,
        components: componentsTranslations.pl,
        company_settings_tabs: companySettingsTabs.pl,
        tasks: taskTranslations.pl,
        program_preview: programPreviewTranslations.pl,
        program_schedule: programScheduleTranslations.pl,
        patient: patientTranslations.pl,
        integrations: integration.pl,
        empty_states: empty_states.pl,
        settings: settings.pl,
        cooperation_settings: cooperation_settings.pl,
        educational_materials: educationalMaterials.pl,
        autogenerate_modal: autogenerate.pl,
        shared: sharedTranslations.pl,
        questionnaires: questionnairesTranslations.pl,
        chat: chatTranslations.pl,
        subscriptions: subscriptions.pl,
        ai_onboarding: aiOnboarding.pl,
        patient_files: patientFiles.pl,
        ai_notes: aiNotes.pl,
        program_pdf: programPdf.pl,
      },
      common: common.pl,
      auth: auth.pl,
      validation: validation.pl,
    },
    en: {
      translation: {
        ...deepMerge(uiEn, en as Record<string, unknown>),
        common: { ...uiEn.common, ...common.en },
        diet: diet.en,
        schedule: schedule.en,
        monitoring: monitoringTranslations.en,
        components: componentsTranslations.en,
        company_settings_tabs: companySettingsTabs.en,
        tasks: taskTranslations.en,
        program_preview: programPreviewTranslations.en,
        program_schedule: programScheduleTranslations.en,
        patient: patientTranslations.en,
        integrations: integration.en,
        empty_states: empty_states.en,
        settings: settings.en,
        cooperation_settings: cooperation_settings.en,
        educational_materials: educationalMaterials.en,
        autogenerate_modal: autogenerate.en,
        shared: sharedTranslations.en,
        questionnaires: questionnairesTranslations.en,
        chat: chatTranslations.en,
        subscriptions: subscriptions.en,
        ai_onboarding: aiOnboarding.en,
        patient_files: patientFiles.en,
        ai_notes: aiNotes.en,
        program_pdf: programPdf.en,
      },
      common: common.en,
      auth: auth.en,
      validation: validation.en,
    },
  },
  lng: "pl",
  fallbackLng: "pl",
  keySeparator: ".",
  debug: false,
  interpolation: {
    escapeValue: false,
  },
  defaultNS: "translation",
  ns: ["translation", "auth", "common", "validation"],
});

export default i18next;
